import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import type { TargetType, ScreenGroup } from '@seencast/shared';
import { TARGET_TYPES } from '@seencast/shared';
import type { Schedule } from '../../api/schedules.ts';
import { Modal } from '../../components/Modal.tsx';
import { Button, Field, SelectField, TextField } from '../../components/ui.tsx';
import { useScreens } from '../../api/screens.ts';
import { useGroups } from '../../api/groups.ts';
import { usePlaylists } from '../../api/playlists.ts';
import { useLayouts } from '../../api/layouts.ts';
import { useSchedules, useCreateSchedule, useUpdateSchedule } from '../../api/schedules.ts';
import { ApiError } from '../../api/client.ts';
import { scheduleWindowSummary, conflictSummary } from '../../lib/schedule-summary.ts';
import { findTopConflict, type ConflictWindow } from '../../lib/schedule-conflicts.ts';
import type { ScreenListItem } from '@seencast/shared';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
const PRESETS: Array<{ label: string; mask: number }> = [
  { label: 'Every day', mask: 0b1111111 },
  { label: 'Weekdays', mask: 0b0011111 },
  { label: 'Weekends', mask: 0b1100000 },
];

function screensForTarget(
  type: TargetType,
  id: string | null,
  screens: ScreenListItem[],
  groups: ScreenGroup[],
): Set<string> {
  if (type === 'org') return new Set(screens.map((s) => s.id));
  if (type === 'screen') return new Set(id ? [id] : []);
  return new Set(groups.find((g) => g.id === id)?.screen_ids ?? []);
}

// Prefill for the "create from an empty calendar slot" flow — the picked target,
// the clicked day, and the clicked hour window.
export interface ScheduleInitial {
  playlistId?: string;
  targetType?: TargetType;
  targetId?: string | null;
  daysMask?: number;
  startTime?: string;
  endTime?: string;
  priority?: number;
}

export function ScheduleForm({
  schedule,
  initial,
  onClose,
}: {
  schedule?: Schedule;
  initial?: ScheduleInitial;
  onClose: () => void;
}) {
  const screens = useScreens();
  const groups = useGroups();
  const playlists = usePlaylists();
  const layouts = useLayouts();
  const existing = useSchedules();
  const create = useCreateSchedule();
  const update = useUpdateSchedule();
  const editing = !!schedule;

  const [playlistId, setPlaylistId] = useState(schedule?.playlist_id ?? initial?.playlistId ?? '');
  const [targetType, setTargetType] = useState<TargetType>(
    schedule?.target_type ?? initial?.targetType ?? 'screen',
  );
  const [targetId, setTargetId] = useState<string | null>(schedule?.target_id ?? initial?.targetId ?? null);
  const [startDate, setStartDate] = useState(schedule?.start_date ?? '');
  const [endDate, setEndDate] = useState(schedule?.end_date ?? '');
  const [daysMask, setDaysMask] = useState(schedule?.days_mask ?? initial?.daysMask ?? 0b1111111);
  const [startTime, setStartTime] = useState(schedule?.start_time ?? initial?.startTime ?? '');
  const [endTime, setEndTime] = useState(schedule?.end_time ?? initial?.endTime ?? '');
  const [priority, setPriority] = useState(schedule?.priority ?? initial?.priority ?? 0);
  const [enabled, setEnabled] = useState(schedule?.enabled ?? true);
  // Schedule-level layout override (Sprint 10). '' = no override (each screen keeps
  // its own layout); a layout id forces that layout while this schedule is active.
  const [layoutId, setLayoutId] = useState<string>(schedule?.layout_id ?? '');
  const [submitted, setSubmitted] = useState(false);

  const targetValid = targetType === 'org' || !!targetId;
  const canSubmit = !!playlistId && targetValid && daysMask !== 0;

  const windowText = scheduleWindowSummary({
    days_mask: daysMask,
    start_time: startTime || null,
    end_time: endTime || null,
    start_date: startDate || null,
    end_date: endDate || null,
  });

  // Live conflict hint.
  const conflict = useMemo(() => {
    if (!screens.data || !groups.data || !existing.data || !playlists.data) return null;
    const proposed: ConflictWindow = {
      id: schedule?.id,
      days_mask: daysMask,
      start_time: startTime || null,
      end_time: endTime || null,
      start_date: startDate || null,
      end_date: endDate || null,
      priority,
      target_type: targetType,
      screens: screensForTarget(targetType, targetId, screens.data, groups.data),
    };
    const others: ConflictWindow[] = existing.data.map((s) => ({
      id: s.id,
      days_mask: s.days_mask,
      start_time: s.start_time,
      end_time: s.end_time,
      start_date: s.start_date,
      end_date: s.end_date,
      priority: s.priority,
      target_type: s.target_type,
      created_at: s.created_at,
      playlist_name: playlists.data!.find((p) => p.id === s.playlist_id)?.name,
      screens: screensForTarget(s.target_type, s.target_id, screens.data!, groups.data!),
    }));
    return findTopConflict(proposed, others);
  }, [screens.data, groups.data, existing.data, playlists.data, daysMask, startTime, endTime, startDate, endDate, priority, targetType, targetId, schedule?.id]);

  function submit() {
    setSubmitted(true);
    if (!canSubmit) return;
    const body = {
      playlist_id: playlistId,
      target_type: targetType,
      target_id: targetType === 'org' ? null : targetId,
      start_date: startDate || null,
      end_date: endDate || null,
      days_mask: daysMask,
      start_time: startTime || null,
      end_time: endTime || null,
      priority,
      enabled,
      layout_id: layoutId || null,
    };
    const onDone = {
      onSuccess: () => {
        toast.success(editing ? 'Schedule updated' : 'Schedule created');
        onClose();
      },
      onError: (e: unknown) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
    };
    if (editing) update.mutate({ id: schedule!.id, body }, onDone);
    else create.mutate(body, onDone);
  }

  const busy = create.isPending || update.isPending;

  return (
    <Modal
      title={editing ? 'Edit schedule' : 'New schedule'}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit || busy}>
            {busy ? 'Saving…' : editing ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="field-row">
        <SelectField
          label="Target"
          value={targetType}
          onChange={(e) => {
            setTargetType(e.target.value as TargetType);
            setTargetId(null);
          }}
        >
          {TARGET_TYPES.map((t) => (
            <option key={t} value={t}>
              {t === 'org' ? 'Whole organization' : t === 'group' ? 'Group' : 'Single screen'}
            </option>
          ))}
        </SelectField>

        {targetType !== 'org' && (
          <SelectField
            label={targetType === 'group' ? 'Group' : 'Screen'}
            value={targetId ?? ''}
            onChange={(e) => setTargetId(e.target.value || null)}
            error={submitted && !targetValid ? 'Pick a target' : undefined}
          >
            <option value="">Select…</option>
            {(targetType === 'group' ? groups.data ?? [] : screens.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </SelectField>
        )}
      </div>

      <SelectField
        label="Playlist"
        value={playlistId}
        onChange={(e) => setPlaylistId(e.target.value)}
        error={submitted && !playlistId ? 'Pick a playlist' : undefined}
      >
        <option value="">Select a playlist…</option>
        {(playlists.data ?? []).map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </SelectField>

      <SelectField
        label="Layout"
        value={layoutId}
        onChange={(e) => setLayoutId(e.target.value)}
        hint="Overrides each screen's own layout while this schedule is active."
      >
        <option value="">Default — each screen's own layout</option>
        {(layouts.data ?? []).map((l) => (
          <option key={l.id} value={l.id}>
            {l.name}
          </option>
        ))}
      </SelectField>

      <Field label="Days" error={submitted && daysMask === 0 ? 'Pick at least one day' : undefined}>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          {DAYS.map((d, i) => {
            const on = !!(daysMask & (1 << i));
            return (
              <button
                key={d}
                type="button"
                className={`chip selectable${on ? ' selected' : ''}`}
                aria-pressed={on}
                onClick={() => setDaysMask(daysMask ^ (1 << i))}
              >
                {d}
              </button>
            );
          })}
          <span className="spacer" />
          {PRESETS.map((p) => (
            <button key={p.label} type="button" className="btn ghost sm" onClick={() => setDaysMask(p.mask)}>
              {p.label}
            </button>
          ))}
        </div>
      </Field>

      <div className="field-row">
        <Field label="Start time" hint="Leave blank for all day">
          <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
        </Field>
        <Field label="End time" hint="Overnight windows are allowed">
          <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
        </Field>
      </div>

      <div className="field-row">
        <Field label="Start date" hint="Blank = start now">
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </Field>
        <Field label="End date" hint="Blank = no end">
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </Field>
      </div>

      <div className="field-row">
        <TextField
          label="Priority"
          type="number"
          value={priority}
          onChange={(e) => setPriority(Number(e.target.value))}
          hint="Higher wins when schedules overlap"
        />
        <Field label="Enabled">
          <label className="row" style={{ paddingTop: 8 }}>
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span className="text-dim">Active</span>
          </label>
        </Field>
      </div>

      <div className="hint-box info mt-4">
        <strong>Summary:</strong> {windowText}
      </div>
      {conflict && (
        <div className={`hint-box ${conflict.thisWins ? 'info' : 'warn'} mt-2`}>
          {conflictSummary(conflict.competitor.playlist_name ?? 'another schedule', conflict.thisWins, conflict.reason)}
        </div>
      )}
    </Modal>
  );
}
